import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useAuthStore } from '../store/useAuthStore';
import { allServices } from '../data/services';
import { ServiceItem } from '../types';
import { API_ENDPOINTS } from '../config/api';

const AdminDashboard: React.FC = () => {
  const { t } = useLanguageStore();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuthStore();
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedService, setEditedService] = useState<ServiceItem | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newUnit, setNewUnit] = useState<Partial<ServiceItem>>({
    id: '',
    title: '',
    titleSinhala: '',
    description: '',
    descriptionSinhala: '',
    icon: 'building',
    bgImage: 'linear-gradient(rgba(46, 125, 50, 0.8), rgba(76, 175, 80, 0.8))',
    stats: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingStatKey, setEditingStatKey] = useState<string | null>(null);
  const [tempStatKeyValue, setTempStatKeyValue] = useState<string>('');
  const [editingNewStatKey, setEditingNewStatKey] = useState<string | null>(null);
  const [tempNewStatKeyValue, setTempNewStatKeyValue] = useState<string>('');

  // Fetch services from API
  const fetchServices = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_ENDPOINTS.units, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setServices(data.data);
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      // Only log if it's not a connection error (backend not running)
      if (err.name !== 'AbortError' && !err.message?.includes('Failed to fetch')) {
        console.warn('Error fetching services:', err);
      }
      // Fallback to local data
      setServices(allServices);
      setError('Backend server not running. Using local data. Start the server with: cd server && npm start');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchServices();
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEdit = (service: ServiceItem) => {
    setEditingId(service.id);
    setEditedService({ ...service });
  };

  const handleSave = async () => {
    if (!editedService) return;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      // Save to API
      const response = await fetch(API_ENDPOINTS.singleUnit(editedService.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedService),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state with saved data
        const updated = services.map(s => s.id === editedService.id ? editedService : s);
        setServices(updated);
        setEditingId(null);
        setEditedService(null);
        alert(t('save-success', 'Changes saved successfully!', 'වෙනස්කම් සාර්ථකව සුරකින ලදී!'));
      } else {
        alert(t('save-error', `Failed to save changes: ${data.message}`, `වෙනස්කම් සුරැකීමට අසමත් විය: ${data.message}`));
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError' || err.message?.includes('Failed to fetch')) {
        alert(t('backend-error', 'Backend server is not running. Please start it with: cd server && npm start', 'බැක්එන්ඩ් සර්වරය ක්‍රියාත්මක නොවේ. කරුණාකර එය ආරම්භ කරන්න: cd server && npm start'));
      } else {
        console.error('Error saving service:', err);
        alert(t('save-error', `Failed to save changes: ${err.message || 'Please try again.'}`, `වෙනස්කම් සුරැකීමට අසමත් විය: ${err.message || 'කරුණාකර නැවත උත්සාහ කරන්න.'}`));
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedService(null);
    setIsCreatingNew(false);
    setNewUnit({
      id: '',
      title: '',
      titleSinhala: '',
      description: '',
      descriptionSinhala: '',
      icon: 'building',
      bgImage: 'linear-gradient(rgba(46, 125, 50, 0.8), rgba(76, 175, 80, 0.8))',
      stats: {}
    });
  };

  const handleFieldChange = (field: keyof ServiceItem, value: any) => {
    if (editedService) {
      setEditedService({ ...editedService, [field]: value });
    }
  };

  const handleStatsChange = (key: string, value: any) => {
    if (editedService && editedService.stats) {
      setEditedService({
        ...editedService,
        stats: { ...editedService.stats, [key]: value }
      });
    }
  };

  const handleStatsKeyChange = (oldKey: string, newKey: string) => {
    if (editedService && editedService.stats) {
      // Don't allow empty keys
      if (!newKey.trim()) {
        return;
      }
      
      // If the new key is the same as old key, do nothing
      if (newKey === oldKey) {
        return;
      }
      
      // Check if new key already exists (and it's not the same as old key)
      if (editedService.stats[newKey] !== undefined && newKey !== oldKey) {
        alert(t('duplicate-key', 'A field with this name already exists', 'මෙම නම සහිත ක්ෂේත්‍රයක් දැනටමත් පවතී'));
        return;
      }
      
      const { [oldKey]: value, ...rest } = editedService.stats;
      setEditedService({
        ...editedService,
        stats: { ...rest, [newKey]: value }
      });
    }
  };

  const handleStatKeyInputChange = (oldKey: string, newValue: string) => {
    setTempStatKeyValue(newValue);
  };

  const handleStatKeyBlur = (oldKey: string) => {
    if (tempStatKeyValue && tempStatKeyValue !== oldKey) {
      handleStatsKeyChange(oldKey, tempStatKeyValue);
    }
    setEditingStatKey(null);
    setTempStatKeyValue('');
  };

  const handleStatKeyFocus = (key: string) => {
    setEditingStatKey(key);
    setTempStatKeyValue(key);
  };

  const handleStatKeyKeyDown = (e: React.KeyboardEvent, oldKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (tempStatKeyValue && tempStatKeyValue !== oldKey) {
        handleStatsKeyChange(oldKey, tempStatKeyValue);
      }
      setEditingStatKey(null);
      setTempStatKeyValue('');
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditingStatKey(null);
      setTempStatKeyValue('');
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleAddStat = () => {
    if (editedService) {
      const newKey = `newField${Object.keys(editedService.stats || {}).length + 1}`;
      setEditedService({
        ...editedService,
        stats: { ...(editedService.stats || {}), [newKey]: '' }
      });
    }
  };

  const handleDeleteStat = (key: string) => {
    if (editedService && editedService.stats) {
      const { [key]: _, ...rest } = editedService.stats;
      setEditedService({
        ...editedService,
        stats: rest
      });
    }
  };

  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setEditingId(null);
    setEditedService(null);
  };

  const handleSaveNew = async () => {
    if (!newUnit.id || !newUnit.title || !newUnit.titleSinhala) {
      alert(t('validation-error', 'Please fill in all required fields (ID, Title, Title Sinhala)', 'කරුණාකර සියලුම අවශ්‍ය ක්ෂේත්‍ර පුරවන්න (ID, ශීර්ෂය, ශීර්ෂය සිංහල)'));
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(API_ENDPOINTS.units, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUnit),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Refresh the services list
        await fetchServices();
        setIsCreatingNew(false);
        setNewUnit({
          id: '',
          title: '',
          titleSinhala: '',
          description: '',
          descriptionSinhala: '',
          icon: 'building',
          bgImage: 'linear-gradient(rgba(46, 125, 50, 0.8), rgba(76, 175, 80, 0.8))',
          stats: {}
        });
        alert(t('create-success', 'Unit created successfully!', 'ඒකකය සාර්ථකව නිර්මාණය කරන ලදී!'));
      } else {
        alert(t('create-error', `Failed to create unit: ${data.message}`, `ඒකකය නිර්මාණය කිරීමට අසමත් විය: ${data.message}`));
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError' || err.message?.includes('Failed to fetch')) {
        alert(t('backend-error', 'Backend server is not running. Please start it with: cd server && npm start', 'බැක්එන්ඩ් සර්වරය ක්‍රියාත්මක නොවේ. කරුණාකර එය ආරම්භ කරන්න: cd server && npm start'));
      } else {
        console.error('Error creating unit:', err);
        alert(t('create-error', `Failed to create unit: ${err.message || 'Please try again.'}`, `ඒකකය නිර්මාණය කිරීමට අසමත් විය: ${err.message || 'කරුණාකර නැවත උත්සාහ කරන්න.'}`));
      }
    }
  };

  const handleDelete = async (unitId: string, unitTitle: string) => {
    if (!window.confirm(
      t('delete-confirm', `Are you sure you want to delete "${unitTitle}"? This action cannot be undone.`, `ඔබට "${unitTitle}" මකා දැමීමට අවශ්‍යද? මෙම ක්‍රියාව අවලංගු කළ නොහැක.`)
    )) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(API_ENDPOINTS.singleUnit(unitId), {
        method: 'DELETE',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Refresh the services list
        await fetchServices();
        alert(t('delete-success', 'Unit deleted successfully!', 'ඒකකය සාර්ථකව මකා දමන ලදී!'));
      } else {
        alert(t('delete-error', `Failed to delete unit: ${data.message}`, `ඒකකය මකා දැමීමට අසමත් විය: ${data.message}`));
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError' || err.message?.includes('Failed to fetch')) {
        alert(t('backend-error', 'Backend server is not running. Please start it with: cd server && npm start', 'බැක්එන්ඩ් සර්වරය ක්‍රියාත්මක නොවේ. කරුණාකර එය ආරම්භ කරන්න: cd server && npm start'));
      } else {
        console.error('Error deleting unit:', err);
        alert(t('delete-error', `Failed to delete unit: ${err.message || 'Please try again.'}`, `ඒකකය මකා දැමීමට අසමත් විය: ${err.message || 'කරුණාකර නැවත උත්සාහ කරන්න.'}`));
      }
    }
  };

  const handleNewUnitFieldChange = (field: keyof ServiceItem, value: any) => {
    setNewUnit({ ...newUnit, [field]: value });
  };

  const handleNewUnitStatsChange = (key: string, value: any) => {
    setNewUnit({
      ...newUnit,
      stats: { ...(newUnit.stats || {}), [key]: value }
    });
  };

  const handleNewUnitStatsKeyChange = (oldKey: string, newKey: string) => {
    if (newUnit.stats) {
      // Don't allow empty keys
      if (!newKey.trim()) {
        return;
      }
      
      // If the new key is the same as old key, do nothing
      if (newKey === oldKey) {
        return;
      }
      
      // Check if new key already exists (and it's not the same as old key)
      if (newUnit.stats[newKey] !== undefined && newKey !== oldKey) {
        alert(t('duplicate-key', 'A field with this name already exists', 'මෙම නම සහිත ක්ෂේත්‍රයක් දැනටමත් පවතී'));
        return;
      }
      
      const { [oldKey]: value, ...rest } = newUnit.stats;
      setNewUnit({
        ...newUnit,
        stats: { ...rest, [newKey]: value }
      });
    }
  };

  const handleNewStatKeyInputChange = (oldKey: string, newValue: string) => {
    setTempNewStatKeyValue(newValue);
  };

  const handleNewStatKeyBlur = (oldKey: string) => {
    if (tempNewStatKeyValue && tempNewStatKeyValue !== oldKey) {
      handleNewUnitStatsKeyChange(oldKey, tempNewStatKeyValue);
    }
    setEditingNewStatKey(null);
    setTempNewStatKeyValue('');
  };

  const handleNewStatKeyFocus = (key: string) => {
    setEditingNewStatKey(key);
    setTempNewStatKeyValue(key);
  };

  const handleNewStatKeyKeyDown = (e: React.KeyboardEvent, oldKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (tempNewStatKeyValue && tempNewStatKeyValue !== oldKey) {
        handleNewUnitStatsKeyChange(oldKey, tempNewStatKeyValue);
      }
      setEditingNewStatKey(null);
      setTempNewStatKeyValue('');
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditingNewStatKey(null);
      setTempNewStatKeyValue('');
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleAddNewStat = () => {
    const newKey = `newField${Object.keys(newUnit.stats || {}).length + 1}`;
    setNewUnit({
      ...newUnit,
      stats: { ...(newUnit.stats || {}), [newKey]: '' }
    });
  };

  const handleDeleteNewStat = (key: string) => {
    if (newUnit.stats) {
      const { [key]: _, ...rest } = newUnit.stats;
      setNewUnit({
        ...newUnit,
        stats: rest
      });
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-text-dark mb-2">
              {t('admin-dashboard', 'Admin Dashboard', 'පරිපාලන උපකරණ පුවරුව')}
            </h1>
            <p className="text-text-secondary">
              {t('manage-units', 'Manage and customize hospital units', 'රෝහල් ඒකක කළමනාකරණය සහ අභිරුචිකරණය')}
            </p>
          </div>
          <div className="flex gap-3">
            {!isCreatingNew && !editingId && (
              <motion.button
                onClick={handleCreateNew}
                className="flex items-center gap-2 bg-primary-green text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus className="w-5 h-5" />
                {t('create-unit', 'Create New Unit', 'නව ඒකකයක් නිර්මාණය කරන්න')}
              </motion.button>
            )}
            <motion.button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-error-red text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut className="w-5 h-5" />
              {t('logout', 'Logout', 'පිටවීම')}
            </motion.button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('loading', 'Loading services...', 'සේවා පූරණය වෙමින්...')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            {error}
          </div>
        )}

        {/* Create New Unit Form */}
        {isCreatingNew && (
          <motion.div
            className="bg-white rounded-xl p-6 shadow-lg border-2 border-primary-green mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl font-bold text-text-dark mb-4">
              {t('create-new-unit', 'Create New Unit', 'නව ඒකකයක් නිර්මාණය කරන්න')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  {t('unit-id', 'Unit ID', 'ඒකක ID')} <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={newUnit.id}
                  onChange={(e) => handleNewUnitFieldChange('id', e.target.value)}
                  placeholder="e.g., new-unit"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                />
                <p className="text-xs text-gray-500 mt-1">{t('unit-id-hint', 'Use lowercase letters, numbers, and hyphens only', 'කුඩා අකුරු, අංක සහ යටිඅඩිය පමණක් භාවිතා කරන්න')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  {t('title-en', 'Title (English)', 'ශීර්ෂය (ඉංග්‍රීසි)')} <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={newUnit.title}
                  onChange={(e) => handleNewUnitFieldChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  {t('title-si', 'Title (Sinhala)', 'ශීර්ෂය (සිංහල)')} <span className="text-error-red">*</span>
                </label>
                <input
                  type="text"
                  value={newUnit.titleSinhala}
                  onChange={(e) => handleNewUnitFieldChange('titleSinhala', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  {t('description-en', 'Description (English)', 'විස්තරය (ඉංග්‍රීසි)')}
                </label>
                <textarea
                  value={newUnit.description}
                  onChange={(e) => handleNewUnitFieldChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  {t('description-si', 'Description (Sinhala)', 'විස්තරය (සිංහල)')}
                </label>
                <textarea
                  value={newUnit.descriptionSinhala}
                  onChange={(e) => handleNewUnitFieldChange('descriptionSinhala', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                />
              </div>
              
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-text-dark">
                    {t('stats', 'Statistics', 'සංඛ්‍යාන')}
                  </h4>
                  <motion.button
                    onClick={handleAddNewStat}
                    className="flex items-center gap-1 text-xs bg-primary-green text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Plus className="w-3 h-3" />
                    Add Field
                  </motion.button>
                </div>
                {newUnit.stats && Object.entries(newUnit.stats).map(([key, value], index) => (
                      <div key={`new-unit-stat-${index}-${key}`} className="bg-gray-50 p-3 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Field Name
                            </label>
                            <input
                              type="text"
                              value={editingNewStatKey === key ? tempNewStatKeyValue : key}
                              onChange={(e) => handleNewStatKeyInputChange(key, e.target.value)}
                              onFocus={() => handleNewStatKeyFocus(key)}
                              onBlur={() => handleNewStatKeyBlur(key)}
                              onKeyDown={(e) => handleNewStatKeyKeyDown(e, key)}
                              placeholder="e.g., Operating Hours"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                            />
                          </div>
                      <motion.button
                        onClick={() => handleDeleteNewStat(key)}
                        className="mt-6 p-2 text-error-red hover:bg-error-red/10 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Delete field"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Value
                      </label>
                      <textarea
                        value={String(value)}
                        onChange={(e) => handleNewUnitStatsChange(key, e.target.value)}
                        rows={3}
                        placeholder="Enter value..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green resize-y"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <motion.button
                  onClick={handleSaveNew}
                  className="flex-1 bg-primary-green text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  {t('create', 'Create', 'නිර්මාණය කරන්න')}
                </motion.button>
                <motion.button
                  onClick={handleCancel}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <X className="w-4 h-4" />
                  {t('cancel', 'Cancel', 'අවලංගු')}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {!loading && services.map((service) => (
            <motion.div
              key={service.id}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {editingId === service.id && editedService ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-dark mb-1">
                      {t('title-en', 'Title (English)', 'ශීර්ෂය (ඉංග්‍රීසි)')}
                    </label>
                    <input
                      type="text"
                      value={editedService.title}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-dark mb-1">
                      {t('title-si', 'Title (Sinhala)', 'ශීර්ෂය (සිංහල)')}
                    </label>
                    <input
                      type="text"
                      value={editedService.titleSinhala}
                      onChange={(e) => handleFieldChange('titleSinhala', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-dark mb-1">
                      {t('description-en', 'Description (English)', 'විස්තරය (ඉංග්‍රීසි)')}
                    </label>
                    <textarea
                      value={editedService.description}
                      onChange={(e) => handleFieldChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-dark mb-1">
                      {t('description-si', 'Description (Sinhala)', 'විස්තරය (සිංහල)')}
                    </label>
                    <textarea
                      value={editedService.descriptionSinhala}
                      onChange={(e) => handleFieldChange('descriptionSinhala', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                    />
                  </div>
                  
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-text-dark">
                        {t('stats', 'Statistics', 'සංඛ්‍යාන')}
                      </h4>
                      <motion.button
                        onClick={handleAddStat}
                        className="flex items-center gap-1 text-xs bg-primary-green text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Plus className="w-3 h-3" />
                        Add Field
                      </motion.button>
                    </div>
                    {editedService.stats && Object.entries(editedService.stats).map(([key, value], index) => (
                      <div key={`${editingId}-stat-${index}-${key}`} className="bg-gray-50 p-3 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Field Name
                            </label>
                            <input
                              type="text"
                              value={editingStatKey === key ? tempStatKeyValue : key}
                              onChange={(e) => handleStatKeyInputChange(key, e.target.value)}
                              onFocus={() => handleStatKeyFocus(key)}
                              onBlur={() => handleStatKeyBlur(key)}
                              onKeyDown={(e) => handleStatKeyKeyDown(e, key)}
                              placeholder="e.g., Operating Hours, Our Times"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green"
                            />
                          </div>
                          <motion.button
                            onClick={() => handleDeleteStat(key)}
                            className="mt-6 p-2 text-error-red hover:bg-error-red/10 rounded-lg transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Delete field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Value
                          </label>
                          <textarea
                            value={String(value)}
                            onChange={(e) => handleStatsChange(key, e.target.value)}
                            rows={3}
                            placeholder="Enter value (press Enter for new lines)..."
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green resize-y"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <motion.button
                      onClick={handleSave}
                      className="flex-1 bg-primary-green text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Save className="w-4 h-4" />
                      {t('save', 'Save', 'සුරකින්න')}
                    </motion.button>
                    <motion.button
                      onClick={handleCancel}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <X className="w-4 h-4" />
                      {t('cancel', 'Cancel', 'අවලංගු')}
                    </motion.button>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-text-dark mb-2">
                    {service.title}
                  </h3>
                  <p className="text-text-secondary text-sm mb-4">
                    {service.titleSinhala}
                  </p>
                  <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  
                  <div className="flex gap-2">
                    <motion.button
                      onClick={() => handleEdit(service)}
                      className="flex-1 bg-primary-green text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Edit2 className="w-4 h-4" />
                      {t('edit', 'Edit', 'සංස්කරණය')}
                    </motion.button>
                    <motion.button
                      onClick={() => handleDelete(service.id, service.title)}
                      className="bg-error-red text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      title={t('delete-unit', 'Delete Unit', 'ඒකකය මකන්න')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-text-secondary">
            {t('admin-note', 'Note: Changes are saved locally. All unit details can be customized here.', 'සටහන: වෙනස්කම් දේශීයව සුරක්ෂිත වේ. සියලුම ඒකක විස්තර මෙහි අභිරුචිකරණය කළ හැකිය.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

